import { Button, Modal, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconFile, IconSparkles } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { UnifiedDocument } from 'src/suitability-report/hooks/useAllClientDocuments';
import PageLoader from 'src/UI/loader/PageLoader';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { EmailDraftAttachment } from '../chat/types';
import { useConversationGeneratedDocuments } from '../chats/useGeneratedDocuments';

// Microsoft Graph rejects inline attachments above ~3 MB; the backend enforces the same cap.
const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;

const DOCUMENT_TYPE_LABELS: Record<UnifiedDocument['documentType'], string> = {
  'suitability-report': 'Suitability report',
  'meeting-agenda': 'Meeting agenda',
  'data-export': 'Data export',
  'meeting-note': 'Meeting note',
  'data-upload': 'Uploaded document',
};

type AttachableDocument = {
  key: string;
  fileId: number;
  fileName: string;
  fileSize: number | null;
  label: string;
  generated: boolean;
};

type Props = {
  opened: boolean;
  onClose: () => void;
  onSelect: (attachment: EmailDraftAttachment) => void;
  clientId?: string;
  familyId?: string;
  // Set when documents Siesta generated in this conversation may be attached too.
  conversationId?: string;
};

export function EmailAttachmentPicker({
  opened,
  onClose,
  onSelect,
  clientId,
  familyId,
  conversationId,
}: Props) {
  // Queried directly rather than via useAllClientDocuments: that hook requires a
  // CurrentClientProvider, which the sidebar (rendered on every page) is not always inside.
  const {
    data: documents = [],
    isLoading: isLoadingDocuments,
    isError: isDocumentsError,
  } = useQuery<UnifiedDocument[]>({
    queryKey: familyId
      ? ['families', familyId, 'all-documents']
      : ['clients', clientId, 'all-documents'],
    enabled: opened && !!(familyId || clientId),
    staleTime: 30000,
  });

  const {
    generatedDocuments,
    isLoading: isLoadingGenerated,
    isError: isGeneratedError,
  } = useConversationGeneratedDocuments(conversationId, opened);

  const isLoading = isLoadingDocuments || isLoadingGenerated;
  const isError = isDocumentsError || isGeneratedError;

  const generatedRows: AttachableDocument[] = generatedDocuments.map(document => ({
    key: `generated-${document.fileId}`,
    fileId: document.fileId,
    fileName: document.fileName,
    fileSize: document.fileSize,
    label: 'Generated by Siesta',
    generated: true,
  }));

  const clientRows: AttachableDocument[] = documents.flatMap(document =>
    document.fileId && document.file
      ? [
          {
            key: `document-${document.id}`,
            fileId: document.fileId,
            fileName: document.file.fileName,
            fileSize: document.file.fileSize ?? null,
            label: DOCUMENT_TYPE_LABELS[document.documentType],
            generated: false,
          },
        ]
      : [],
  );

  const handleSelect = (document: AttachableDocument) => {
    onSelect({ fileId: document.fileId, fileName: document.fileName });
    onClose();
  };

  const renderRow = (document: AttachableDocument) => {
    const tooLarge = (document.fileSize ?? 0) > MAX_ATTACHMENT_BYTES;
    const Icon = document.generated ? IconSparkles : IconFile;
    const row = (
      <UnstyledButton
        key={document.key}
        onClick={() => handleSelect(document)}
        disabled={tooLarge}
        p="xs"
        style={{
          borderRadius: 'var(--mantine-radius-sm)',
          border: '1px solid var(--mantine-color-gray-3)',
          opacity: tooLarge ? 0.5 : undefined,
          width: '100%',
        }}
      >
        <Stack gap={2}>
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9" truncate>
            <Icon size={13} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} aria-hidden />
            {document.fileName}
          </Text>
          <Text size="textXs" c="gray.6">
            {document.label}
          </Text>
        </Stack>
      </UnstyledButton>
    );
    return tooLarge ? (
      <Tooltip key={document.key} label="Too large to attach (over 3 MB)">
        {row}
      </Tooltip>
    ) : (
      row
    );
  };

  const hasRows = generatedRows.length > 0 || clientRows.length > 0;

  return (
    <Modal opened={opened} onClose={onClose} title="Attach a document" size="md">
      {isLoading && <PageLoader absolute={false} />}
      {isError && (
        <Text size="textSm" c="red.7">
          The documents could not be loaded. Please try again.
        </Text>
      )}
      {!isLoading && !isError && !hasRows && (
        <Text size="textSm" c="gray.7">
          There are no documents available to attach.
        </Text>
      )}
      {!isLoading && !isError && hasRows && (
        <Stack gap="sm">
          {generatedRows.length > 0 && (
            <Stack gap="xs">
              {clientRows.length > 0 && (
                <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.7">
                  Generated in this chat
                </Text>
              )}
              {generatedRows.map(renderRow)}
            </Stack>
          )}
          {clientRows.length > 0 && (
            <Stack gap="xs">
              {generatedRows.length > 0 && (
                <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.7">
                  {familyId ? 'Household documents' : 'Client documents'}
                </Text>
              )}
              {clientRows.map(renderRow)}
            </Stack>
          )}
        </Stack>
      )}
      <Button variant="subtle" color="gray" size="xs" mt="sm" onClick={onClose}>
        Cancel
      </Button>
    </Modal>
  );
}
